import React, { Suspense, lazy, useEffect } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import Loader from 'components/Loader';
import { IS_SANDBOX_ENV, IS_SANDBOX_LOGIN_ACCESSIBLE } from 'helper/constant';
import PropTypes from 'prop-types';
import IntegrationPage from 'views/V2/Integration';
import ReNewPassword from 'views/V2/LoginFlow/ReNewPassword';

import LoginRoute from './LoginRoutes';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import { RedirectRoute } from './RedirectRoute';
import endPoints from './endpoints';

// Backward compatibility: if user go to old billing dashboard route, redirect it
// to billing tab in settings page. Query string (e.g. ?topUp=success from the
// Stripe checkout redirect) must stay before the #billing hash, since
// Settings.jsx reads the tab name from window.location.hash.
const BillingDashboardRedirect = ({ location }) => (
    <Redirect to={`${endPoints.settings}${location.search}#billing`} />
);

BillingDashboardRedirect.propTypes = {
    location: PropTypes.shape({
        search: PropTypes.string,
    }).isRequired,
};

//Layout
const Dashboard = lazy(() => import('layouts/V2/Dashboard'));

//screen
const Login = lazy(() => import('views/V2/LoginFlow/Login/index'));
const TwoFA = lazy(() => import('views/V2/LoginFlow/TwoFA'));
const ResetPassword = lazy(() => import('views/V2/LoginFlow/ResetPassword'));
const VerifyEmail = lazy(() => import('views/VerifyEmail'));
const Signup = lazy(() => import('views/V2/SignUp'));
const Home = lazy(() => import('views/V2/Home'));
const MedallionPage = lazy(() => import('views/V2/Medallion'));
const NotFound = lazy(() => import('views/NotFound'));
const SettingsV2 = lazy(() => import('views/V2/Settings'));
const TermsAndPrivacy = lazy(() => import('views/V2/TermsAndPrivacy'));
const EmbeddedDocument = lazy(() =>
    import('views/V2/Home/components/SignDocument/EmbeddedDocument')
);
const Users = lazy(() => import('views/V2/Users'));
const Tcm = lazy(() => import('views/V2/TCM'));
const Logout = lazy(() => import('views/Logout'));
const SandboxLogin = lazy(() => import('views/SandboxLogin'));
const SandboxLoader = lazy(() => import('views/SandboxLoader'));
const UserProfile = lazy(() => import('views/V2/UserProfile/UserProfileRoute'));

const UserProfileWrapper = ({ match }) => <UserProfile match={match} />;

const Routes = () => {
    const location = useLocation();

    useEffect(() => {
        if (window?._hsq) {
            window._hsq.push(['setPath', location.pathname]);
            window._hsq.push(['trackPageView']);
        }
    }, [location]);
    return (
        <Suspense fallback={<Loader />}>
            <Switch>
                {/* Block specific routes in sandbox environment */}
                {!IS_SANDBOX_ENV && (
                    <LoginRoute path={endPoints.default} exact ComponentNew={Login} />
                )}

                {!IS_SANDBOX_ENV && (
                    <LoginRoute path={endPoints.twoFA} exact ComponentNew={TwoFA} />
                )}

                {!IS_SANDBOX_ENV && (
                    <PublicRoute path={endPoints.register} component={Signup} exact />
                )}

                {!IS_SANDBOX_ENV && (
                    <PublicRoute
                        path={endPoints.consent}
                        component={() => <TermsAndPrivacy />}
                        exact
                    />
                )}

                <LoginRoute path={endPoints.resetPassword} exact ComponentNew={ResetPassword} />

                <LoginRoute
                    path={`${endPoints.setResetPassword}/:token`}
                    exact
                    ComponentNew={ReNewPassword}
                    type="forgot-password"
                />

                <LoginRoute
                    path={`${endPoints.setInvitePassword}/:token`}
                    exact
                    ComponentNew={ReNewPassword}
                    type="invite-password"
                />

                <PublicRoute
                    path={`${endPoints.verifyEmail}`}
                    component={() => <VerifyEmail />}
                    exact
                />

                {IS_SANDBOX_LOGIN_ACCESSIBLE && (
                    <PublicRoute
                        path={endPoints.sandboxLogin}
                        component={() => <SandboxLogin />}
                        allowAuthenticated
                        exact
                    />
                )}

                {IS_SANDBOX_ENV && (
                    <PublicRoute
                        path={endPoints.default}
                        component={() => <SandboxLoader />}
                        exact
                    />
                )}

                <PrivateRoute path={`${endPoints.users}/:uuid`} component={UserProfileWrapper} />

                <PrivateRoute
                    path={endPoints.userSearch}
                    component={() => <Redirect to={endPoints.users} />}
                    exact
                />

                <PrivateRoute
                    path={endPoints.dashboard}
                    component={() => (
                        <Dashboard>
                            <Home />
                        </Dashboard>
                    )}
                />

                <PrivateRoute
                    path={endPoints.users}
                    component={() => (
                        <Dashboard>
                            <Users />
                        </Dashboard>
                    )}
                />

                <PrivateRoute
                    path={endPoints.billingDashboard}
                    component={BillingDashboardRedirect}
                    exact
                />

                <PrivateRoute path={endPoints.workflows} component={MedallionPage} />
                <PrivateRoute path={endPoints.integration} component={IntegrationPage} />
                <PrivateRoute
                    path={endPoints.settings}
                    component={() => (
                        <Dashboard>
                            <SettingsV2 />
                        </Dashboard>
                    )}
                    exact
                />

                <PrivateRoute
                    path={endPoints.tcm}
                    component={() => (
                        <Dashboard>
                            <Tcm />
                        </Dashboard>
                    )}
                />

                <PrivateRoute
                    path={endPoints.signDocument}
                    component={() => <EmbeddedDocument />}
                    exact
                />

                <PrivateRoute path={endPoints.logout} component={() => <Logout />} />

                <Route
                    path="/index.html"
                    component={() => <RedirectRoute pathname="/" includeQueryParams={true} />}
                />
                <Route path="*" component={NotFound} />
            </Switch>
        </Suspense>
    );
};

UserProfileWrapper.propTypes = {
    match: PropTypes.object.isRequired,
};

export default Routes;
